'use client'

import { useEffect, useMemo, useState } from 'react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  createStep,
  defaultSteps,
  graphToSteps,
  stepsToGraph,
  validateStepIssues,
  type Step,
} from '@/lib/commands/graph-serialize'
import { AddStepMenu } from './AddStepMenu'
import { StepCard } from './StepCard'

export function GraphBuilder({
  value,
  onChange,
  error,
  onErrorChange,
  includeEvent = false,
}: {
  value: Record<string, unknown> | null
  onChange: (graph: Record<string, unknown>) => void
  error: string | null
  onErrorChange: (err: string | null) => void
  includeEvent?: boolean
}) {
  const initial = parseInitialSteps(value)
  const [steps, setSteps] = useState<Step[]>(initial.steps)
  const [expandedStepId, setExpandedStepId] = useState<string | null>(initial.steps.find((step) => step.kind !== 'end')?.id ?? null)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [jsonText, setJsonText] = useState(() => stringifyJson(value ?? stepsToGraph(initial.steps)))
  const [jsonError, setJsonError] = useState<string | null>(initial.error)
  const [lastSyncedGraph, setLastSyncedGraph] = useState(() => stringifyJson(value ?? stepsToGraph(initial.steps)))

  const issues = useMemo(() => validateStepIssues(steps), [steps])
  const issueMessages = useMemo(() => Array.from(new Set(issues.map((issue) => issue.message))), [issues])
  const errorsByStep = useMemo(() => {
    const grouped = new Map<string, string[]>()
    for (const issue of issues) {
      if (!issue.stepId) continue
      grouped.set(issue.stepId, [...(grouped.get(issue.stepId) ?? []), issue.message])
    }
    return grouped
  }, [issues])
  const readOnly = Boolean(jsonError)

  useEffect(() => {
    const nextGraph = stepsToGraph(steps) as Record<string, unknown>
    const nextText = stringifyJson(nextGraph)
    if (!jsonError && nextText !== lastSyncedGraph) {
      setJsonText(nextText)
      setLastSyncedGraph(nextText)
      onChange(nextGraph)
    }
    onErrorChange(jsonError ?? (issueMessages.length > 0 ? issueMessages.join('\n') : null))
  }, [issueMessages, jsonError, lastSyncedGraph, onChange, onErrorChange, steps])

  useEffect(() => {
    if (!value) return
    const incoming = stringifyJson(value)
    if (incoming === lastSyncedGraph || incoming === jsonText) return
    const parsed = graphToSteps(value)
    if ('error' in parsed) {
      setJsonError(parsed.error)
      setJsonText(incoming)
      setLastSyncedGraph(incoming)
      return
    }
    setSteps(parsed)
    setJsonText(incoming)
    setJsonError(null)
    setLastSyncedGraph(incoming)
  }, [jsonText, lastSyncedGraph, value])

  const applySteps = (nextSteps: Step[], expandedId = expandedStepId) => {
    setSteps(nextSteps)
    setExpandedStepId(expandedId)
    setJsonError(null)
  }

  const addStep = (kind: Step['kind']) => {
    const step = createStep(kind, steps)
    const endIndex = steps.findIndex((candidate) => candidate.kind === 'end')
    const insertIndex = endIndex >= 0 && kind !== 'end' ? endIndex : steps.length
    applySteps([...steps.slice(0, insertIndex), step, ...steps.slice(insertIndex)], step.kind === 'end' ? expandedStepId : step.id)
  }

  const updateStep = (nextStep: Step) => {
    applySteps(steps.map((step) => step.id === nextStep.id ? nextStep : step))
  }

  const moveStep = (stepId: string, direction: 'up' | 'down') => {
    const index = steps.findIndex((step) => step.id === stepId)
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (index < 0 || targetIndex < 0 || targetIndex >= steps.length) return
    const nextSteps = [...steps]
    const [step] = nextSteps.splice(index, 1)
    if (!step) return
    nextSteps.splice(targetIndex, 0, step)
    applySteps(nextSteps)
  }

  const duplicateStep = (step: Step) => {
    if (step.kind === 'end') return
    const duplicate = { ...step, id: createStep(step.kind, steps).id } as Step
    const index = steps.findIndex((candidate) => candidate.id === step.id)
    applySteps([...steps.slice(0, index + 1), duplicate, ...steps.slice(index + 1)], duplicate.id)
  }

  const deleteStep = (stepId: string) => {
    const step = steps.find((candidate) => candidate.id === stepId)
    if (!step) return
    if (step.kind === 'end' && steps.filter((candidate) => candidate.kind === 'end').length === 1) return
    const nextSteps = steps.filter((candidate) => candidate.id !== stepId)
    applySteps(nextSteps.length > 0 ? nextSteps : defaultSteps(), null)
  }

  const applyJsonText = (text: string) => {
    setJsonText(text)
    try {
      const parsed = parseJsonObject(text)
      const parsedSteps = graphToSteps(parsed)
      if ('error' in parsedSteps) {
        setJsonError(parsedSteps.error)
        return
      }
      setSteps(parsedSteps)
      setJsonError(null)
      setLastSyncedGraph(stringifyJson(parsed))
      onChange(parsed)
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : 'Invalid JSON')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Label>Steps</Label>
        <AddStepMenu hasEnd={steps.some((step) => step.kind === 'end')} onAdd={addStep} />
      </div>

      {(jsonError || error) && (
        <Alert className="border-amber-300 bg-amber-50 text-amber-950">
          <AlertDescription>
            JSON has errors. Fix it below to re-enable builder.
            <span className="mt-1 block text-sm">{jsonError ?? error}</span>
          </AlertDescription>
        </Alert>
      )}

      {steps.length === 0 ? (
        <div className="rounded-lg border border-dashed border-atrium-border bg-atrium-content p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-atrium-black/60">Add your first step to get started.</p>
            <AddStepMenu hasEnd={false} onAdd={addStep} />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {steps.map((step, index) => (
            <StepCard
              key={step.id}
              step={step}
              index={index}
              steps={steps}
              expanded={expandedStepId === step.id}
              readOnly={readOnly}
              errors={errorsByStep.get(step.id) ?? []}
              includeEvent={includeEvent}
              onToggleExpanded={() => setExpandedStepId(expandedStepId === step.id ? null : step.id)}
              onChange={updateStep}
              onMove={(direction) => moveStep(step.id, direction)}
              onDuplicate={() => duplicateStep(step)}
              onDelete={() => deleteStep(step.id)}
            />
          ))}
        </div>
      )}

      {issueMessages.length > 0 && !jsonError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          {issueMessages.map((message) => (
            <p key={message} className="text-sm text-red-600">{message}</p>
          ))}
        </div>
      )}

      <div className="border-t border-atrium-border pt-3">
        <Button
          type="button"
          variant="ghost"
          className="px-0 text-atrium-black"
          onClick={() => setAdvancedOpen((open) => !open)}
        >
          Advanced (edit JSON) {advancedOpen ? '▴' : '▾'}
        </Button>
        {advancedOpen && (
          <div className="mt-3 space-y-2">
            <Textarea
              className="min-h-48 font-mono"
              value={jsonText}
              onChange={(event) => applyJsonText(event.target.value)}
            />
            {jsonError ? <p className="text-sm text-red-600">{jsonError}</p> : null}
          </div>
        )}
      </div>
    </div>
  )
}

function parseInitialSteps(value: Record<string, unknown> | null): { steps: Step[]; error: string | null } {
  if (!value) return { steps: defaultSteps(), error: null }
  const parsed = graphToSteps(value)
  if ('error' in parsed) return { steps: defaultSteps(), error: parsed.error }
  return { steps: parsed, error: null }
}

function stringifyJson(value: Record<string, unknown>) {
  return JSON.stringify(value, null, 2)
}

function parseJsonObject(value: string) {
  const parsed = JSON.parse(value)
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('JSON must be an object')
  }
  return parsed as Record<string, unknown>
}
